<template>
  <div
    v-if="isDevelopment"
    class="dev-toggle"
    data-testid="dev-mode-toggle"
    :style="{
      left: position.x + 'px',
      top: position.y + 'px',
    }"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <div class="dev-toggle-header" data-testid="dev-toggle-header">
      <span class="dev-icon">🔧</span>
      <span class="dev-title">DEV</span>
      <button class="expand-btn" @click="toggleExpanded">
        {{ isExpanded ? '−' : '+' }}
      </button>
    </div>

    <div v-if="isExpanded" class="dev-toggle-content">
      <div class="dev-section">
        <h4>PWA Mode</h4>
        <div class="toggle-row">
          <label class="toggle-label">
            <input
              type="checkbox"
              :checked="pwaStore.isInstalled.value"
              class="toggle-input"
              @change="togglePWAMode"
            />
            <span class="toggle-slider"></span>
            <span class="toggle-text">{{
              pwaStore.isInstalled.value ? 'Installed' : 'Browser'
            }}</span>
          </label>
        </div>
        <p class="mode-description">
          {{
            pwaStore.isInstalled.value
              ? 'Shows bottom navigation, hides drawer'
              : 'Shows drawer navigation, hides bottom nav'
          }}
        </p>
      </div>

      <div class="dev-section">
        <h4>Network Status</h4>
        <div class="status-indicator">
          <span
            class="status-dot"
            :class="{ online: pwaStore.isOnline.value }"
          ></span>
          <span>{{ pwaStore.isOnline.value ? 'Online' : 'Offline' }}</span>
          <button class="small-btn" @click="toggleOnlineStatus">Toggle</button>
        </div>
      </div>

      <div class="dev-section">
        <h4>User Type</h4>
        <div class="user-select-container">
          <select
            v-model="selectedUserType"
            class="user-select"
            :disabled="isChangingUser"
            @change="changeUserType"
            @mousedown.stop
            @touchstart.stop
          >
            <option value="">None (Logged out)</option>
            <option value="customer">Customer</option>
            <option value="service-provider">Service Provider</option>
            <option value="admin-super">Super Admin</option>
            <option value="admin-manager">Manager</option>
          </select>
          <span v-if="isChangingUser" class="changing-indicator">🔄</span>
        </div>
      </div>

      <div class="dev-section">
        <h4>Quick Actions</h4>
        <div class="action-buttons">
          <button class="action-btn" @click="clearCache">Clear Cache</button>
          <button class="action-btn" @click="simulateUpdate">
            Simulate Update
          </button>
          <button class="action-btn" @click="triggerNotification">
            Test Notification
          </button>
        </div>
      </div>

      <div class="dev-section">
        <h4>Device Simulation</h4>
        <div class="device-buttons">
          <button
            v-for="device in devices"
            :key="device.name"
            class="device-btn"
            :class="{ active: currentDevice === device.name }"
            @click="simulateDevice(device)"
          >
            {{ device.name }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { usePWA } from '@/composables/usePWA'
import { createLogger } from '@/utils/logger'
import type { User } from '@/api/types'

const logger = createLogger('DevModeToggle')

// Development mode detection (but not during E2E tests)
const isDevelopment = computed(() => {
  // Don't show during E2E tests (Playwright sets navigator.webdriver to true)
  if (navigator.webdriver) {
    return false
  }
  return (
    import.meta.env.DEV || localStorage.getItem('force-dev-mode') === 'true'
  )
})

const pwaStore = usePWA()
const isExpanded = ref(false)
const selectedUserType = ref('')
const currentDevice = ref('Desktop')
const isChangingUser = ref(false)

// Dragging functionality
const position = reactive({ x: 20, y: 100 })
const isDragging = ref(false)
const dragOffset = reactive({ x: 0, y: 0 })

const devices = [
  { name: 'iPhone', width: 390, height: 844 },
  { name: 'Android', width: 412, height: 869 },
  { name: 'iPad', width: 768, height: 1024 },
  { name: 'Desktop', width: 1200, height: 800 },
]

// Mock user data for different types (updated to multi-role system)
// Note: Mock users match authState User type (REST API format with snake_case)
// This is intentional because authState.user comes from REST login endpoint
const mockUsers: Record<string, User> = {
  customer: {
    user_id: 'dev-customer-1',
    user_type: 'customer',
    user_roles: [],
    email: 'dev.customer@example.com',
    first_name: 'Dev',
    last_name: 'Customer',
  },
  'service-provider': {
    user_id: 'dev-provider-1',
    user_type: 'employee',
    user_roles: ['service-provider'],
    email: 'dev.provider@example.com',
    first_name: 'Dev',
    last_name: 'Provider',
  },
  'admin-super': {
    user_id: 'dev-admin-1',
    user_type: 'employee',
    user_roles: ['admin-super'],
    email: 'dev.admin@example.com',
    first_name: 'Dev',
    last_name: 'Admin',
  },
  'admin-manager': {
    user_id: 'dev-manager-1',
    user_type: 'employee',
    user_roles: ['admin-manager'],
    email: 'dev.manager@example.com',
    first_name: 'Dev',
    last_name: 'Manager',
  },
}

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
  // Save expanded state
  localStorage.setItem('dev-toggle-expanded', String(isExpanded.value))
}

const togglePWAMode = () => {
  // ONLY allow PWA mode override in development environment
  if (!isDevelopment.value) {
    logger.warn('PWA mode toggle is only available in development mode')
    return
  }

  // Override the PWA installed state for dev purposes
  const newState = !pwaStore.isInstalled.value

  // Store the override in localStorage (only works in dev mode)
  localStorage.setItem('dev-pwa-installed', String(newState))
  logger.log(`🔧 DEV: Set localStorage dev-pwa-installed = ${newState}`)

  if (newState) {
    // When enabling PWA mode, automatically set a default user if none exists
    const existingUser = localStorage.getItem('dev-user')
    if (!existingUser) {
      const defaultUser = mockUsers['customer']
      localStorage.setItem('auth_token', 'dev-token-customer')
      localStorage.setItem('dev-user', JSON.stringify(defaultUser))
      selectedUserType.value = 'customer'
      logger.log('🔧 DEV: Auto-set default customer user for PWA mode')
    }
  } else {
    // When disabling PWA mode, optionally clear user (for clean browser mode)
    // Keep user for now to allow testing both modes with same user
  }

  // Force update the reactive value
  ;(pwaStore.isInstalled as any).value = newState

  // Trigger a visual update
  document.body.classList.toggle('dev-pwa-mode', newState)

  // Trigger custom event to notify other components
  window.dispatchEvent(
    new CustomEvent('pwa-mode-change', { detail: { isInstalled: newState } })
  )

  logger.log(`🔧 DEV: PWA mode ${newState ? 'enabled' : 'disabled'}`)
  logger.log(`🔧 DEV: isInstalled.value = ${pwaStore.isInstalled.value}`)
}

const toggleOnlineStatus = () => {
  const newState = !pwaStore.isOnline.value
  ;(pwaStore.isOnline as any).value = newState

  // Simulate online/offline events
  window.dispatchEvent(new Event(newState ? 'online' : 'offline'))

  logger.log(
    `🔧 DEV: Network status changed to ${newState ? 'online' : 'offline'}`
  )
}

const changeUserType = async () => {
  logger.log(`🔧 DEV: Changing user type to: ${selectedUserType.value}`)

  // Set loading state
  isChangingUser.value = true

  if (selectedUserType.value === '') {
    logger.log('🔧 DEV: Logging out...')
    // Simulate logout
    localStorage.removeItem('auth_token')
    localStorage.removeItem('dev-user')

    // Add small delay for user feedback
    await new Promise(resolve => setTimeout(resolve, 800))

    // Trigger auth change event before reload
    window.dispatchEvent(new CustomEvent('auth-change'))
    window.location.reload()
  } else {
    // Simulate login with mock user
    const mockUser = mockUsers[selectedUserType.value]
    logger.log(
      `🔧 DEV: Switching to user type: ${selectedUserType.value}`,
      mockUser
    )

    localStorage.setItem('auth_token', `dev-token-${selectedUserType.value}`)
    localStorage.setItem('dev-user', JSON.stringify(mockUser))

    // Add small delay for user feedback
    await new Promise(resolve => setTimeout(resolve, 800))

    // Trigger auth change event before reload
    window.dispatchEvent(new CustomEvent('auth-change'))
    window.location.reload()
  }
}

const clearCache = async () => {
  try {
    // Clear service worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map(name => caches.delete(name)))
    }

    // Clear localStorage except dev settings
    const devSettings = {
      'dev-pwa-installed': localStorage.getItem('dev-pwa-installed'),
      'force-dev-mode': localStorage.getItem('force-dev-mode'),
    }
    localStorage.clear()
    Object.entries(devSettings).forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value)
    })

    logger.log('🔧 DEV: Cache cleared')
    alert('Cache cleared successfully!')
  } catch (error) {
    logger.error('Failed to clear cache:', error)
  }
}

const simulateUpdate = () => {
  logger.log('🔧 DEV: Simulating app update')
  alert(
    'App update simulation - in a real PWA this would trigger the update flow'
  )
}

const triggerNotification = async () => {
  if (!('Notification' in window)) {
    alert('Notifications not supported in this browser')
    return
  }

  if (Notification.permission === 'granted') {
    new Notification('🔧 Dev Test Notification', {
      body: 'This is a test notification from the dev console',
      icon: '/icon-192.png',
      badge: '/icon-192.png',
    })
  } else if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission()
    if (permission === 'granted') {
      triggerNotification()
    }
  } else {
    alert('Notifications are blocked. Please enable them in browser settings.')
  }
}

const simulateDevice = (device: (typeof devices)[0]) => {
  currentDevice.value = device.name

  // Apply device dimensions (simplified simulation)
  const viewport = document.querySelector('meta[name="viewport"]')
  if (viewport && device.name !== 'Desktop') {
    logger.log(
      `🔧 DEV: Simulating ${device.name} (${device.width}x${device.height})`
    )

    // In a real implementation, you might use Chrome DevTools Protocol
    // For now, just log the simulation
    alert(
      `Simulating ${device.name} - Check browser dev tools for responsive view`
    )
  }
}

// Drag functionality
const startDrag = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true

  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

  dragOffset.x = clientX - position.x
  dragOffset.y = clientY - position.y

  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchmove', onDrag)
  document.addEventListener('touchend', stopDrag)

  e.preventDefault()
}

const onDrag = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return

  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

  position.x = Math.max(
    0,
    Math.min(window.innerWidth - 300, clientX - dragOffset.x)
  )
  position.y = Math.max(
    0,
    Math.min(window.innerHeight - 100, clientY - dragOffset.y)
  )
}

const stopDrag = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)

  // Save position
  localStorage.setItem('dev-toggle-position', JSON.stringify(position))
}

// Initialize
onMounted(() => {
  // Restore position
  const savedPosition = localStorage.getItem('dev-toggle-position')
  if (savedPosition) {
    Object.assign(position, JSON.parse(savedPosition))
  }

  // Restore expanded state
  const savedExpanded = localStorage.getItem('dev-toggle-expanded')
  if (savedExpanded === 'true') {
    isExpanded.value = true
  }

  // Check for dev PWA mode override
  const devPWAMode = localStorage.getItem('dev-pwa-installed')
  if (devPWAMode === 'true') {
    ;(pwaStore.isInstalled as any).value = true
    document.body.classList.add('dev-pwa-mode')
  }

  // Check for mock user
  const devUser = localStorage.getItem('dev-user')
  if (devUser) {
    const user = JSON.parse(devUser)
    // For multi-role system: check user_type and first role
    if (user.user_type === 'customer') {
      selectedUserType.value = 'customer'
    } else if (user.user_roles && user.user_roles.length > 0) {
      selectedUserType.value = user.user_roles[0]
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)
})
</script>

<style scoped>
.dev-toggle {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  border-radius: 12px;
  min-width: 200px;
  max-width: 320px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 12px;
  cursor: move;
  user-select: none;
}

.dev-toggle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px 12px 0 0;
  cursor: move;
}

.dev-icon {
  font-size: 16px;
}

.dev-title {
  font-weight: bold;
  color: #00ff88;
  flex: 1;
  text-align: center;
}

.expand-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.dev-toggle-content {
  padding: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.dev-section {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
}

.dev-section:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.dev-section h4 {
  margin: 0 0 8px 0;
  color: #00ff88;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.toggle-row {
  margin-bottom: 8px;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 8px;
}

.toggle-input {
  display: none;
}

.toggle-slider {
  width: 32px;
  height: 16px;
  background: #333;
  border-radius: 16px;
  position: relative;
  transition: background 0.3s;
}

.toggle-slider::after {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
}

.toggle-input:checked + .toggle-slider {
  background: #00ff88;
}

.toggle-input:checked + .toggle-slider::after {
  transform: translateX(16px);
}

.toggle-text {
  font-size: 11px;
  color: #ccc;
}

.mode-description {
  margin: 4px 0 0 0;
  font-size: 10px;
  color: #888;
  line-height: 1.3;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
  transition: background 0.3s;
}

.status-dot.online {
  background: #00ff88;
}

.small-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.small-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.user-select-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-select {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  position: relative;
  z-index: 10000;
  cursor: pointer;
  pointer-events: auto;
  /* Keep native appearance for dropdown functionality */
}

.user-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.changing-indicator {
  font-size: 12px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.user-select option {
  background: #222;
  color: white;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.device-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
}

.device-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.device-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.device-btn.active {
  background: #00ff88;
  color: black;
}

/* Scrollbar styling */
.dev-toggle-content::-webkit-scrollbar {
  width: 4px;
}

.dev-toggle-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.dev-toggle-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.dev-toggle-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>
